import { getLoadOptionsQuery } from '@utils/loadOptionsConverter';
import { filterKindOf } from '@srm/ui/data-table/filters';
import { IGridItem } from '@models/response/IGridResponse';
import { IDataGridColumnDescription } from '@models/response/IGridDescriptionResponse';
import { IAttachColumn, IAttachFilter, IAttachRow, IAttachSort } from './AttachGrid.types';

/** Служебные колонки схемы — не данные (действия рисуем отдельно, emptyColumn — распорка). */
const SERVICE_FIELDS = new Set(['actionColumn', 'emptyColumn']);
export const isServiceField = (field: string): boolean => SERVICE_FIELDS.has(field);

/** Нормализует колонку схемы в модель грида; null — для служебных колонок. */
export function mapColumn(c: IDataGridColumnDescription): IAttachColumn | null {
    if (isServiceField(c.field)) return null;
    return {
        field: c.field,
        header: c.header,
        dataType: c.dataType,
        width: c.width || undefined,
        minWidth: c.minWidth,
        alignment: c.alignment ?? 'left',
        allowSorting: c.allowSorting,
        allowFiltering: c.allowFiltering,
        filterKind: filterKindOf(c.dataType),
        userKeyField: c.userKeyField,
        isStatusColumn: c.isStatusColumn,
        show: !c.defaultHide,
        showInColumnChooser: c.showInColumnChooser,
        cssClasses: c.cssClasses ?? [],
    };
}

/** Начальная (преднастроенная с бэка) многоуровневая сортировка из sortIndex/sortOrder. */
export function initialSort(columns: IDataGridColumnDescription[]): IAttachSort[] {
    return columns
        .filter((c) => !isServiceField(c.field) && !!c.sortOrder && c.sortIndex != null)
        .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))
        .map((c) => ({ field: c.field, dir: c.sortOrder as 'asc' | 'desc' }));
}

/** DevExtreme-выражение фильтра из активных фильтров колонок (склейка через 'and'). */
function buildFilterExpression(filters: IAttachFilter[]): unknown[] | null {
    const active = filters.filter((f) => f.value !== '' && f.value != null);
    if (!active.length) return null;
    const leaves = active.map((f) => [f.field, f.op, f.value]);
    if (leaves.length === 1) return leaves[0];
    const expr: unknown[] = [];
    leaves.forEach((leaf, i) => {
        if (i) expr.push('and');
        expr.push(leaf);
    });
    return expr;
}

/** Поля для запроса (видимые колонки + поля сортировки) — как в текущем гриде. */
export function fieldsParam(columns: IAttachColumn[], sort: IAttachSort[]): string {
    const set = new Set<string>();
    for (const c of columns) if (c.show) set.add(c.field);
    for (const s of sort) set.add(s.field);
    return Array.from(set).join('||');
}

export interface ILoadOptionsArgs {
    sort: IAttachSort[];
    filters?: IAttachFilter[];
    skip?: number;
    take?: number;
    requireTotalCount?: boolean;
}

/** DevExtreme-образный объект loadOptions (для getLoadOptionsQuery и серверного экспорта). */
export function buildLoadOptions({
    sort,
    filters,
    skip,
    take,
    requireTotalCount,
}: ILoadOptionsArgs): Record<string, unknown> {
    const lo: Record<string, unknown> = {};
    if (skip != null) lo.skip = skip;
    if (take != null) lo.take = take;
    if (requireTotalCount) lo.requireTotalCount = true;
    if (sort.length) lo.sort = sort.map((s) => ({ selector: s.field, desc: s.dir === 'desc' }));
    const filterExpr = filters ? buildFilterExpression(filters) : null;
    if (filterExpr) lo.filter = filterExpr;
    return lo;
}

/** Query-строка для fetchData: '?' + сериализованные loadOptions + '&fields=...'. */
export function buildDataParams(columns: IAttachColumn[], args: ILoadOptionsArgs): string {
    return `?${getLoadOptionsQuery(buildLoadOptions(args))}&fields=${fieldsParam(columns, args.sort)}`;
}

export function toAttachRow(item: IGridItem): IAttachRow {
    return {
        key: item.key,
        documentKey: item.documentKey,
        url: item.url,
        expandable: item.expandable,
        actions: item.actions ?? [],
        fields: item.fields ?? {},
        classes: item.classes ?? [],
        raw: item,
    };
}
